// zod DTOs for the silo module (S7) — BUILD_SPEC.md §5.6, §8.2, §8.6.
import { z } from 'zod';
import { MOVEMENT_SOURCES, STOCK_COUNT_STATUSES } from '../enums.js';
import { paginationResponseSchema, quantitySchema, ulidSchema } from './common.js';
// ---- Silo-module-local enums (no pre-existing entry in enums.ts to mirror) ----
export const SILO_LEVEL_UNITS = ['PCT', 'M'];
export const siloLevelUnitSchema = z.enum(SILO_LEVEL_UNITS);
// REVERSED mirrors packages/db/src/schema/silo.ts's siloReadingStatusEnum
// (migration 0005, spec-parity audit fix 2026-07-07: readings gained
// POST /:id/reverse). This zod mirror lagged the db enum until the web's
// reverse UI needed the type (2026-07-10).
export const SILO_READING_STATUSES = ['DRAFT', 'POSTED', 'REVERSED'];
export const siloReadingStatusSchema = z.enum(SILO_READING_STATUSES);
// STOCK_COUNT_STATUSES (+ its `StockCountStatus` type) already live in ../enums.ts
// (the canonical source, ratified there ahead of this goal) and flow through this
// barrel via `export * from '../enums.js'` — only the zod schema is new here (no
// `export type StockCountStatus` redeclaration: that would collide with enums.ts's
// export in schemas/index.ts's `export *` barrel). packages/db/src/schema/silo.ts
// has no @zebra/core dependency (see that file's header) so it duplicates the
// literal value list instead; a test in services/api/src/modules/silo asserts the
// two stay in sync.
export const stockCountStatusSchema = z.enum(STOCK_COUNT_STATUSES);
const movementSourceSchema = z.enum(MOVEMENT_SOURCES);
// ---- Silo readings (§8.2) ----
export const createSiloReadingRequestSchema = z.object({
    locationId: ulidSchema,
    productId: ulidSchema,
    readAt: z.coerce.date(),
    levelValue: quantitySchema,
    levelUnit: siloLevelUnitSchema,
    source: movementSourceSchema,
});
// GET /silo/readings list query (same listQueryBaseSchema idiom as intake.ts's
// ticketListQuerySchema — cursor/limit plus optional filters). `status` exists
// so current-state consumers (the web's latest-reading-per-silo card) can ask
// for POSTED only — a REVERSED reading must not present as a silo's current
// level; unfiltered listing still returns every status for the register.
export const listSiloReadingsQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
    locationId: ulidSchema.optional(),
    status: siloReadingStatusSchema.optional(),
});
export const siloReadingResponseSchema = z.object({
    id: ulidSchema,
    tenantId: ulidSchema,
    siteId: ulidSchema,
    locationId: ulidSchema,
    productId: ulidSchema,
    readAt: z.coerce.date(),
    levelValue: z.string(),
    levelUnit: siloLevelUnitSchema,
    estVolumeM3: z.string(),
    densityUsedKgM3: z.string(),
    estMassKg: z.string(),
    ledgerQtyKg: z.string(),
    varianceKg: z.string(),
    variancePct: z.string().nullable(),
    adjustmentMovementId: ulidSchema.nullable(),
    status: siloReadingStatusSchema,
    source: movementSourceSchema,
    enteredBy: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const listSiloReadingsResponseSchema = paginationResponseSchema(siloReadingResponseSchema);
export const postSiloReadingReconciliationResponseSchema = z.object({
    reading: siloReadingResponseSchema,
    adjustmentMovementId: ulidSchema.nullable(),
    alreadyPosted: z.boolean(),
});
// POST /silo/readings/:id/reverse (services/api modules/silo/readings.ts) —
// same {document, reversedMovementIds} shape as sales.ts's
// dispatchReverseResponseSchema. `reversedMovementIds` is empty for a
// zero-variance reading (its /post recorded no movement, so the reversal is a
// pure status flip).
export const reverseSiloReadingResponseSchema = z.object({
    reading: siloReadingResponseSchema,
    reversedMovementIds: z.array(ulidSchema),
});
// ---- Stock counts (§8.6) ----
export const createStockCountRequestSchema = z.object({
    siteId: ulidSchema,
    scope: z.record(z.string(), z.unknown()).optional(),
});
export const stockCountResponseSchema = z.object({
    id: ulidSchema,
    tenantId: ulidSchema,
    siteId: ulidSchema,
    number: z.string(),
    status: stockCountStatusSchema,
    scope: z.record(z.string(), z.unknown()).nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const listStockCountsResponseSchema = paginationResponseSchema(stockCountResponseSchema);
export const upsertStockCountLineRequestSchema = z.object({
    productId: ulidSchema,
    locationId: ulidSchema,
    countedQty: quantitySchema.nullable().optional(),
    handlingUnitsCounted: z.number().int().nullable().optional(),
    note: z.string().nullable().optional(),
});
export const stockCountLineResponseSchema = z.object({
    id: ulidSchema,
    countId: ulidSchema,
    productId: ulidSchema,
    locationId: ulidSchema,
    expectedQty: z.string(),
    expectedHandlingUnits: z.number().int().nullable(),
    countedQty: z.string().nullable(),
    handlingUnitsCounted: z.number().int().nullable(),
    note: z.string().nullable(),
});
export const stockCountWithLinesResponseSchema = stockCountResponseSchema.extend({
    lines: z.array(stockCountLineResponseSchema),
});
export const completeStockCountResponseSchema = z.object({
    count: stockCountResponseSchema,
    adjustmentMovementIds: z.array(ulidSchema),
    alreadyCompleted: z.boolean(),
});
// POST /silo/counts/:id/populate (services/api modules/silo/counts.ts) — bulk-inserts
// one count line per current stock_levels row at the count's site, snapshotting
// expected values exactly as the line-upsert's first-save does. Zero-qty/zero-
// handling stock_levels rows are excluded from the query entirely (nothing worth
// counting) so they never appear in this response; `skippedExisting` counts only
// qualifying rows that already had a count line (the `onConflictDoNothing` skip).
export const populateStockCountResponseSchema = z.object({
    inserted: z.number().int().nonnegative(),
    skippedExisting: z.number().int().nonnegative(),
    totalLines: z.number().int().nonnegative(),
});
//# sourceMappingURL=silo.js.map